import { Trans } from "@lingui/react/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { DeleteEpisodeDialog } from "~/components/episodes/delete-episode-dialog";
import { UnpublishEpisodeDialog } from "~/components/episodes/unpublish-episode-dialog";
import { Button } from "~/components/ui/button";
import {
	FormCard,
	FormCardBody,
	FormCardHeader,
	FormCardHeaderSubtitle,
	FormCardHeaderTitle,
} from "~/components/ui/form-card";
import { Label } from "~/components/ui/label";
import { isChannelManagedExternally } from "~/models/channel";
import { EpisodeStatus } from "~/models/episode";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute("/_app/podcasts/$channelUid/episodes/$episodeUid/settings")({
	component: Page,
});

function Page() {
	const { channelUid, episodeUid } = Route.useParams();
	const trpc = useTRPC();

	const { data: channel } = useSuspenseQuery(
		trpc.channel.find.queryOptions({
			channelUid,
		}),
	);

	const { data: episode } = useSuspenseQuery(
		trpc.episode.find.queryOptions({
			episodeUid,
		}),
	);

	const isManagedExternally = isChannelManagedExternally(channel);

	return (
		<div className="grid w-full gap-6 px-4">
			<FormCard className="border-destructive">
				<FormCardHeader>
					<div>
						<FormCardHeaderTitle>
							<Trans>Danger Zone</Trans>
						</FormCardHeaderTitle>
						<FormCardHeaderSubtitle>
							<Trans>Careful with this section, it can permanently affect your podcast.</Trans>
						</FormCardHeaderSubtitle>
					</div>
				</FormCardHeader>
				<FormCardBody>
					<section className="space-y-6">
						{episode.statusID === EpisodeStatus.Published && (
							<div className="grid gap-2">
								<div className="flex gap-3">
									<div className="flex grow flex-col gap-2">
										<div className="flex-1">
											<div className="flex items-center justify-between">
												<Label>
													<Trans>Unpublish this episode</Trans>
												</Label>
											</div>
											<div className="text-muted-foreground mt-1 text-sm">
												{isManagedExternally ? (
													<Trans>
														This episode is managed externally and cannot be unpublished.
													</Trans>
												) : (
													<Trans>
														This will remove the episode from your feed and change it's status to
														Draft. You can republish this episode later.
													</Trans>
												)}
											</div>
										</div>
									</div>
									{isManagedExternally ? (
										<Button disabled variant="destructive">
											Unpublish
										</Button>
									) : (
										<UnpublishEpisodeDialog episodeUid={episodeUid} />
									)}
								</div>
							</div>
						)}
						{episode.statusID === EpisodeStatus.Removed ? (
							<div className="grid gap-2">
								<div className="flex gap-3">
									<div className="flex grow flex-col gap-2">
										<div className="flex-1">
											<div className="flex items-center justify-between">
												<Label>
													<Trans>Restore this episode</Trans>
												</Label>
											</div>
											<div className="text-muted-foreground mt-1 text-sm">
												<Trans>This episode is deleted and cannot be restored.</Trans>
											</div>
										</div>
									</div>
									<Button disabled variant="outline">
										<Trans>Restore</Trans>
									</Button>
								</div>
							</div>
						) : (
							<div className="grid gap-2">
								<div className="flex gap-3">
									<div className="flex grow flex-col gap-2">
										<div className="flex-1">
											<div className="flex items-center justify-between">
												<Label>
													<Trans>Delete this episode</Trans>
												</Label>
											</div>
											<div className="text-muted-foreground mt-1 text-sm">
												{isManagedExternally ? (
													<Trans>This episode is managed externally and cannot be deleted.</Trans>
												) : (
													<Trans>
														Once you delete an episode, it's removed from your feed and the status
														is changed to archived.
													</Trans>
												)}
											</div>
										</div>
									</div>
									{isManagedExternally ? (
										<Button disabled variant="destructive">
											Delete
										</Button>
									) : (
										<DeleteEpisodeDialog episodeUid={episodeUid} />
									)}
								</div>
							</div>
						)}
					</section>
				</FormCardBody>
			</FormCard>
		</div>
	);
}
